/*
 * Kleurkiezer voor het passe-partout bij een wissellijst.
 *
 * Twee taken:
 *  1. Het label 'Kleur passe-partout: <naam>' gelijk houden aan het gekozen bolletje.
 *  2. De juiste productfoto tonen bij de gekozen combinatie lijstkleur + passe-partoutkleur.
 *
 * Dat tweede deed Shopify vroeger zelf, toen elke combinatie een eigen variant was. Nu de
 * passe-partoutkleur een line item property is, kent de variant alleen nog de lijstkleur en
 * blijft de foto op de passe-partoutkleur van de variantfoto staan. De koppeling combinatie →
 * foto komt uit de bestandsnamen en staat als JSON in de snippet.
 *
 * Dawn hertekent bij een variantwissel alleen de variantkiezer, de prijs en de knoppen, dus dit
 * blok blijft staan en houdt zijn keuze.
 *
 * Met data-combo-fotos blijft van alle combinatiefoto's alleen die van de gekozen combinatie in
 * de galerij staan (miniaturen en schuifbalk op de telefoon). Sfeerfoto's hebben een andere
 * bestandsnaam en blijven gewoon zichtbaar.
 */
if (!customElements.get('passepartout-kleur')) {
  customElements.define(
    'passepartout-kleur',
    class PassepartoutKleur extends HTMLElement {
      connectedCallback() {
        this.valueTarget = this.querySelector('[data-pp-kleur-value]');
        this.fotos = this.leesFotos();
        this.frame = this.dataset.frame || '';
        this.framePosition = parseInt(this.dataset.framePosition, 10) || 1;
        this.comboFotos = (this.dataset.comboFotos || '').split(',').filter(Boolean);

        this.addEventListener('change', this.onChange);
        this.updateLabel();

        // Bij het laden staat de variantfoto in beeld, en die hoort bij één vaste
        // passe-partoutkleur. Meteen gelijktrekken met het bolletje dat aanstaat, anders klopt
        // de foto niet met de keuze die er staat.
        this.synchroniseerFoto();

        // Bij een variantwissel zet Dawn eerst zijn eigen variantfoto; onze correctie moet
        // daarna komen, vandaar het uitstel tot na die herteken-beurt.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event && event.data && event.data.variant;
            if (!variant || !Array.isArray(variant.options)) return;
            const gekozen = variant.options[this.framePosition - 1];
            if (!gekozen) return;
            this.frame = gekozen;
            this.synchroniseerFoto();
          });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        window.clearTimeout(this.synchroniseerTimer);
      }

      onChange = () => {
        this.updateLabel();
        this.synchroniseerFoto();
      };

      leesFotos() {
        const bron = this.querySelector('[data-pp-kleur-fotos]');
        if (!bron) return {};
        try {
          return JSON.parse(bron.textContent) || {};
        } catch (e) {
          return {};
        }
      }

      get gekozenKleur() {
        const checked = this.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }

      updateLabel() {
        if (this.valueTarget && this.gekozenKleur) {
          this.valueTarget.textContent = this.gekozenKleur;
        }
      }

      // Dawn vervangt de galerij bij een variantwissel met een view-transition, dus de nieuwe
      // opmaak staat er pas even later. Eén keer corrigeren is daardoor te vroeg: we proberen
      // het kort opnieuw en stoppen zodra de juiste foto vooraan staat.
      synchroniseerFoto(pogingenOver = 14) {
        this.toonFoto();
        this.verbergAndereCombinaties();
        if (pogingenOver <= 0 || this.fotoKlopt()) return;
        window.clearTimeout(this.synchroniseerTimer);
        this.synchroniseerTimer = window.setTimeout(() => this.synchroniseerFoto(pogingenOver - 1), 150);
      }

      get mediaIdVoorKeuze() {
        const kleur = this.gekozenKleur;
        if (!kleur || !this.frame) return null;
        return this.fotos[this.frame + '||' + kleur] || null;
      }

      fotoKlopt() {
        const mediaId = this.comboFotos.length ? this.comboFotoOmTeHouden : this.mediaIdVoorKeuze;
        // Geen foto van deze combinatie: dan valt er niets te corrigeren en zijn we klaar.
        if (!mediaId) return true;
        const gallery = document.querySelector('media-gallery');
        const item = gallery && gallery.querySelector('[data-media-id$="-' + mediaId + '"]');
        return !!(item && item.classList.contains('is-active'));
      }

      // Welke combinatiefoto blijft staan: die van de gekozen combinatie, anders een foto van
      // dezelfde lijstkleur met een ander passe-partout. Heeft de lijstkleur nog geen foto's
      // (een nieuwe kleur), dan geen enkele: een foto van een andere lijst zou misleiden.
      get comboFotoOmTeHouden() {
        if (this.mediaIdVoorKeuze) return String(this.mediaIdVoorKeuze);
        if (!this.frame) return null;
        const sleutel = Object.keys(this.fotos).find((key) => key.startsWith(this.frame + '||'));
        return sleutel ? String(this.fotos[sleutel]) : null;
      }

      // Verbergt de andere combinatiefoto's. Dawn bouwt de galerij bij een variantwissel opnieuw
      // op, dus dit draait mee met elke synchronisatie.
      verbergAndereCombinaties() {
        if (this.comboFotos.length === 0) return;
        const gallery = document.querySelector('media-gallery');
        if (!gallery) return;

        const houden = this.comboFotoOmTeHouden;

        let gewijzigd = false;
        this.comboFotos.forEach((id) => {
          const verberg = id !== houden;
          gallery
            .querySelectorAll('[data-media-id$="-' + id + '"], [data-target$="-' + id + '"]')
            .forEach((element) => {
              if (element.classList.contains('pp-combo-verborgen') === verberg) return;
              element.classList.toggle('pp-combo-verborgen', verberg);
              gewijzigd = true;
            });
        });

        // Staat er nu een verborgen foto in beeld, dan de eerste zichtbare foto tonen.
        const actief = gallery.querySelector('.product__media-item.is-active');
        if (actief && actief.classList.contains('pp-combo-verborgen')) {
          const eerste = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]')).find(
            (item) => !item.classList.contains('pp-combo-verborgen') && !item.classList.contains('pp-dubbel')
          );
          if (eerste && typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(eerste.dataset.mediaId, true);
          }
        }

        // De schuifbalk op de telefoon telt zijn pagina's opnieuw als er iets verdwijnt.
        const slider = gallery.querySelector('slider-component');
        if (gewijzigd && slider && typeof slider.resetPages === 'function') slider.resetPages();
      }

      toonFoto() {
        const mediaId = this.comboFotos.length ? this.comboFotoOmTeHouden : this.mediaIdVoorKeuze;
        if (!mediaId) return; // van deze combinatie bestaat geen foto — laat staan wat er staat

        const gallery = document.querySelector('media-gallery');
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

        // De id in de opmaak is '<sectie>-<media>', en de sectie-id kennen we hier niet.
        const item = gallery.querySelector('[data-media-id$="-' + mediaId + '"]');
        if (item) gallery.setActiveMedia(item.dataset.mediaId, true);
      }
    }
  );
}
